import { Voucher } from '../types';
import { PdfTablePropsColumn, PdfTablePropsRow } from '../../../components/Pdfs/PdfComponents/PdfTable';
import { BillingContactPDFType } from '../../../components/Pdfs/PdfComponents/PdfBillingContactDetails';
import { taytanFormatNumberToString } from '../../../utils/mathUtils';
import { getDateByFormat } from '../../../utils/dateUtils';
import { getPdfAgentInfo } from '../../travelAgencyAccounts/helpers';
import { PdfInfoBuilderContext, BuiltPdfInfo } from './types';
import { getCurrencyDetails } from './helpers';

// ── Page 1: Service summary columns ──────────────────────────────────────────
// Exported so the inline creation path (Statement.tsx) renders the identical
// table instead of keeping its own copy — the builder is authoritative for PDFs.
export const STATEMENT_COLUMNS: PdfTablePropsColumn[] = [
    { title: 'Service',     fieldName: 'service',              style: { width: '14%' } },
    { title: 'Description', fieldName: 'description',          style: { width: '30%' } },
    { title: 'Fare',        fieldName: 'fareToShow',           style: { width: '14%' } },
    { title: 'Tax',         fieldName: 'taxToShow',            style: { width: '14%' } },
    { title: 'Commission',  fieldName: 'agentCommissionToShow', style: { width: '14%' } },
    { title: 'Currency',    fieldName: 'currency',             style: { width: '14%' } }
];

// ── Page 2: Per-invoice breakdown columns ────────────────────────────────────
export const STATEMENT_INVOICE_COLUMNS: PdfTablePropsColumn[] = [
    { title: 'INV Ref',     fieldName: 'number',              style: { width: '8.5%' } },
    { title: 'Service',     fieldName: 'serviceType',         style: { width: '8.5%' } },
    { title: 'Issue Date',  fieldName: 'createdAt',           style: { width: '8.5%' } },
    { title: 'Value Date',  fieldName: 'dueDate',             style: { width: '8.5%' } },
    { title: 'Description', fieldName: 'for',                 style: { width: '23.5%' } },
    { title: 'T. File',     fieldName: 'travelFileNumber',    style: { width: '8.5%' } },
    { title: 'Fare',        fieldName: 'fareToShow',          style: { width: '8.5%' } },
    { title: 'Tax',         fieldName: 'taxToShow',           style: { width: '8.5%' } },
    { title: 'Commission',  fieldName: 'agentCommissionToShow', style: { width: '8.5%' } },
    { title: 'Net to Remit', fieldName: 'totalToShow',        style: { width: '8.5%' } }
];

/**
 * Build pdfInfo for Statement (Periodic Invoice)
 * Page 1: Service summary table (aggregated from covered invoices)
 * Page 2: Per-invoice breakdown list
 */
export function buildStatementPdfInfo(
    voucher: Voucher,
    context: PdfInfoBuilderContext,
    baseInfo: BuiltPdfInfo
): BuiltPdfInfo {
    const info = voucher.info as any;
    const { code: currencyCode } = getCurrencyDetails(info?.currency, context.currencies);
    const coveredDocuments = context.coveredDocuments || [];

    const columns = STATEMENT_COLUMNS;

    // Aggregate services from covered invoices' stored fields
    // Each invoice has: amount (=fare), tax, commission stored in documentinfo
    const servicesMap: Record<string, { fare: number; tax: number; commission: number }> = {};
    for (const doc of coveredDocuments) {
        const docInfo: any = Array.isArray(doc.info) ? doc.info[0] : doc.info;
        const serviceKey = docInfo?.serviceName || 'General';
        if (!servicesMap[serviceKey]) servicesMap[serviceKey] = { fare: 0, tax: 0, commission: 0 };
        const fare = Number(docInfo?.amount || 0) - Number(docInfo?.tax || 0);
        servicesMap[serviceKey].fare += fare;
        servicesMap[serviceKey].tax += Number(docInfo?.tax || 0);
        servicesMap[serviceKey].commission += Number(docInfo?.commission || 0);
    }

    const rows: PdfTablePropsRow[] = Object.entries(servicesMap).map(([service, totals]) => ({
        service,
        description: 'Does not include VAT',
        fareToShow: taytanFormatNumberToString(totals.fare),
        taxToShow: taytanFormatNumberToString(totals.tax),
        agentCommissionToShow: taytanFormatNumberToString(totals.commission),
        currency: currencyCode
    }));

    const totalFare = Object.values(servicesMap).reduce((acc, v) => acc + v.fare, 0);
    const totalTax = Object.values(servicesMap).reduce((acc, v) => acc + v.tax, 0);
    const totalCommission = Object.values(servicesMap).reduce((acc, v) => acc + v.commission, 0);

    const summaryLine: PdfTablePropsRow = {
        service: 'Total',
        description: '',
        fareToShow: taytanFormatNumberToString(totalFare),
        taxToShow: taytanFormatNumberToString(totalTax),
        agentCommissionToShow: taytanFormatNumberToString(totalCommission),
        currency: currencyCode
    };

    // ── Total box ─────────────────────────────────────────────────────────────
    const amount = Number(info?.amount) || 0;
    const currencySymbol = context.currencies.find(c =>
        c._id === (typeof info?.currency === 'object' ? info.currency?._id : info?.currency)
    )?.symbol || '';
    const pdfTotalBoxData = [
        { label: 'Total',        value: taytanFormatNumberToString(totalFare) + currencySymbol },
        { label: 'Commission -', value: taytanFormatNumberToString(totalCommission) + currencySymbol },
        { label: 'Tax +',        value: taytanFormatNumberToString(totalTax) + currencySymbol },
        { label: 'Net to Remit', value: taytanFormatNumberToString(amount) + currencySymbol }
    ];

    const columnsInvoices = STATEMENT_INVOICE_COLUMNS;

    const rowsInvoices: PdfTablePropsRow[] = coveredDocuments.map((doc) => {
        const docInfo: any = Array.isArray(doc.info) ? doc.info[0] : doc.info;
        const fare = Number(docInfo?.amount || 0) - Number(docInfo?.tax || 0);
        return {
            number: String(doc.number || ''),
            serviceType: docInfo?.serviceName || '',
            createdAt: doc.createdAt ? getDateByFormat(doc.createdAt, 'dd-MMM-yy', '') : '',
            dueDate: docInfo?.dueDate ? getDateByFormat(docInfo.dueDate, 'dd-MMM-yy', '') : '',
            for: docInfo?.for || '',
            travelFileNumber: docInfo?.travelFileNumber || '',
            fareToShow: taytanFormatNumberToString(fare),
            taxToShow: taytanFormatNumberToString(Number(docInfo?.tax || 0)),
            agentCommissionToShow: taytanFormatNumberToString(Number(docInfo?.commission || 0)),
            totalToShow: taytanFormatNumberToString(Number(docInfo?.netToRemit || 0))
        };
    });

    // Build date range for bottomDetails (earliest to latest due date)
    let bottomDetails = info?.for || '';
    if (coveredDocuments.length > 0) {
        const dueDates = coveredDocuments
            .map((d: any) => { const di = Array.isArray(d.info) ? d.info[0] : d.info; return di?.dueDate; })
            .filter(Boolean)
            .map((d: any) => new Date(d))
            .sort((a: Date, b: Date) => a.getTime() - b.getTime());
        if (dueDates.length >= 2) {
            bottomDetails = `${getDateByFormat(dueDates[0], 'dd-MMM-yy', '')} to ${getDateByFormat(dueDates[dueDates.length - 1], 'dd-MMM-yy', '')}`;
        }
    }

    let agentInfo: BillingContactPDFType | null = null;
    if (voucher.sourceType === 'agent' && context.travelAgencyAccount) {
        agentInfo = getPdfAgentInfo(context.travelAgencyAccount);
    }
    if (!agentInfo && info?.travelAgentName) {
        agentInfo = { name: info.travelAgentName, address: '', city: '', country: '', postalCode: '', vatNumber: '', phones: [] };
    }

    return {
        ...baseInfo,
        columns,
        rows,
        summaryLine,
        columnsInvoices,
        rowsInvoices,
        pdfTotalBoxData,
        agentInfo,
        bottomDetails
    };
}
